// Sugalarm — Cycle 391: the shared contract for the "I'm treating this"
// window, so the hook, the sheet and the banner cannot disagree with each
// other or with the server.
//
// THE SERVER IS THE AUTHORITY FOR EVERY VALUE HERE. These constants exist
// so the CLIENT renders the same rules the server enforces — never so the
// client can enforce them on its own. A client that ignored all of this
// still gets a capped window, because `alerts_service.begin_treating` caps
// it server-side; what these buy is that the sheet never offers an option
// the server would refuse, which is the difference between a considered
// product and a button that sometimes fails during a hypo.

/** ⚠ CYCLE 404 RETIRED `TREATING_DURATIONS`, `TreatingDuration`,
 *  `treatingOptionsFor` AND `invalidDurationMessage` — INTENTIONAL,
 *  flagged for `regression-guard`.
 *
 *  There is one duration now, ten minutes, and the SERVER assigns it: the
 *  request field is ignored rather than validated, so there is no set for
 *  a client to be outside of and no refusal sentence to derive. Nobody
 *  standing over a hypo should be estimating durations, and a system that
 *  re-verifies every ten minutes is safer than a caregiver pre-buying
 *  fifteen.
 *
 *  ⚠ CYCLE 427 RETIRED THE CAP TOO — `TREATING_CAPPED_DURATION_SECONDS`,
 *  `TREATING_ENGAGEMENT_CAP` and `TREATING_CAP_LINE` are gone (operator-
 *  authorized 2026-09-08, walkthrough decision 1; supersedes cycle 423's
 *  raise from 2 to 4). Every window is ten minutes, the fifth as much as
 *  the first, and every "yes" is ten more; there is no shortened window
 *  for the client to warn about. INTENTIONAL, flagged for
 *  `regression-guard`. */

/** ⚠ CYCLE 404 — THE ONE WINDOW, in seconds. Ten minutes, every door.
 *
 *  The SERVER is the authority: it assigns this and ignores anything a
 *  client sends. The constant exists only so the optimistic countdown
 *  shows the right number between the tap and the confirming frame. Its
 *  server twin is `alerts_service.CUSTODY_DURATIONS[0]`. */
export const TREATING_WINDOW_SECONDS = 600;

/** Cycle 392 — how long the OPTIONAL name step may sit over a live alarm
 *  before dismissing itself.
 *
 *  ⚠ THIS IS A SAFETY TIMEOUT, NOT A UX FLOURISH. The step is explicitly
 *  optional, so caregivers walk away from it — and its scrim covers the
 *  alarm surface. If a breakthrough tears the window down while it is up,
 *  Snooze would be visible but NOT TAPPABLE behind an unattended prompt.
 *  The step also closes immediately when the window ends; this is the
 *  belt for the case where it simply goes unanswered. */
export const TREATING_IDENTITY_AUTO_DISMISS_MS = 45 * 1000;

/** "10 minutes" — sentence case, no em dash. Derived from the seconds. */
export function treatingDurationLabel(seconds: number): string {
  return `${Math.round(seconds / 60)} minutes`;
}

/** `M:SS` for the banner countdown. Ceils so a live window never reads
 *  0:00 while it is still holding, matching the snooze countdown. */
export function fmtTreatingRemaining(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

/** Pull the machine-readable refusal code out of a failed `ApiResult`.
 *
 *  ⚠ EXTRACTED AND EXPORTED SO IT CAN BE TESTED, because the first version
 *  of this cycle read `r.detail` — a field that does not exist on the
 *  failure shape — so EVERY refusal silently collapsed to the generic
 *  message and the dark-sensor case could never say the sensor was the
 *  reason. The safety floor still held (the server refused and the alarm
 *  kept sounding), but the caregiver-facing half of that defense did not
 *  work at all, and a unit test of the message map alone could never have
 *  caught it.
 *
 *  The real path: `apiClient.readError` puts the parsed body under
 *  `error.details`, and the body is FastAPI's `{"detail": {...}}`. Our
 *  detail is an OBJECT, so `readError` does not promote it to `message`
 *  either — the code only exists here. */
export function treatingErrorCode(err: {
  details?: unknown;
  status?: number;
}): string {
  const details = err.details;
  if (details && typeof details === "object" && "detail" in details) {
    const detail = (details as { detail?: unknown }).detail;
    if (detail && typeof detail === "object" && "code" in detail) {
      const code = (detail as { code?: unknown }).code;
      if (typeof code === "string" && code) return code;
    }
  }
  // A 429 from the in-process limiter carries no structured body.
  if (err.status === 429) return "too_many_requests";
  return "failed";
}

/** What to tell the caregiver when the server refuses.
 *
 *  `no_fresh_reading` is the one that MUST be distinguishable: the alarm
 *  is still sounding and the reason is the sensor, not the button. A
 *  generic failure there would train people to tap again instead of
 *  looking at the feed. */
export function treatingErrorMessage(code: string): string {
  switch (code) {
    case "no_fresh_reading":
      // Cycle 427: no server produces this any more (every door opens on a
      // dark feed). Kept for an older server; still says the alarm keeps
      // going.
      return "No recent reading to check against, so the alarm keeps going.";
    case "not_urgent_low":
      return "Treating is only available on an urgent low.";
    case "episode_closed":
      return "This alarm has already ended.";
    case "too_many_requests":
      return "Too many requests. Try again in a moment.";
    case "not_ready":
      // No server row is bound to this episode yet, so no window can be
      // opened and none may be faked locally. The alarm keeps going.
      return "Not ready yet. The alarm keeps going.";
    default:
      return "Could not start. The alarm keeps going.";
  }
}

// ─── Cycle 429: re-resolve the slot on a stale-row refusal ────────────
//
// On 2026-09-10 twenty-two "I'm treating this" taps were refused
// `not_urgent_low` because the phone's alert slot was still bound to a row
// its dead socket had delivered hours earlier. The tap reached the server
// and was refused for the ROW, not the member. The client's answer is ONE
// retry: ask the server which rows are open for this member right now,
// pick the live urgent-low row, and re-send the same tap to it. If there is
// none, the refusal stands and renders as before.

/** Which of these rows should the retried tap go to? The OPEN (fired,
 *  not recovered, not silenced) `urgent_low` row that is not the one just
 *  refused — the newest by `triggered_at` if there are several. Null when
 *  there is no such row, which means the refusal was right. Pure. */
export function pickLiveUrgentRow(
  rows: readonly {
    id: string;
    level: string;
    fired_at: string | null;
    recovered_at?: string | null;
    silenced_at?: string | null;
    triggered_at: string;
  }[],
  refusedId: string | null,
): (typeof rows)[number] | null {
  let best: (typeof rows)[number] | null = null;
  for (const r of rows) {
    if (r.level !== "urgent_low") continue;
    if (r.fired_at == null || r.recovered_at != null || r.silenced_at != null) continue;
    if (refusedId != null && r.id === refusedId) continue;
    if (best == null) {
      best = r;
      continue;
    }
    const a = new Date(r.triggered_at).getTime();
    const b = new Date(best.triggered_at).getTime();
    if (Number.isFinite(a) && (!Number.isFinite(b) || a > b)) best = r;
  }
  return best;
}

/** The two refusals that mean "wrong row", not "wrong time". Exactly these
 *  earn the one retry; a throttle, a network fault or a validation error
 *  never do. */
export function refusalMeansStaleRow(code: string | null | undefined): boolean {
  return code === "not_urgent_low" || code === "episode_closed";
}

/** ── Cycle 451: WHICH SURFACE IS ON SCREEN — ONE PREDICATE ──────────────
 *
 *  The treating screen is an EARLY RETURN in `NowScreen`, above the
 *  bedside clock face's own branch. That makes "a window is open" and
 *  "the treating screen is the surface on screen" the same fact — and the
 *  app-chrome decision needs it, because cycle 145's chrome suppression
 *  is keyed on the FACE being on screen and had no way to learn the face
 *  had been replaced.
 *
 *  ⚠ THIS IS THE MOUNT GATE ITSELF, NOT A COPY OF IT. `NowScreen` calls
 *  this function to decide whether to return `<TreatingScreen>`, and
 *  `App.tsx` calls the same function to decide whether the clock face is
 *  still the surface behind the chrome. A second copy of the condition
 *  is exactly the mirror-drift this codebase has been bitten by before
 *  (the cycle-393/394 strip reservation note) — so there is one.
 *
 *  ⚠ IT DECIDES NOTHING ABOUT ALARMS. No audio, no custody write, no
 *  recovery, no gate reads this; it answers "what is painted" and
 *  nothing else. Declared custody and a quiet window BOTH mount the
 *  screen (cycle 409), and both are urgent-low only.
 */
export function treatingSurfaceMounted(episode: {
  isLive: boolean;
  treating: boolean;
  quietWindow: boolean;
  episodeLevel: string | null;
  bannerSuppressedInRange: boolean;
}): boolean {
  return (
    episode.isLive &&
    (episode.treating || episode.quietWindow) &&
    episode.episodeLevel === "urgent_low" &&
    !episode.bannerSuppressedInRange
  );
}
